import React, { useMemo, useState } from "react";
import { Box, FormControl, FormLabel, Grid2 } from "@mui/material";
import { Datepicker, locale, MbscDatepickerChangeEvent } from "@mobiscroll/react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { setInputs } from "../../../features/Booking-Form/slices/booking.slice";
import { BookingType, Frequence } from "../../../features/Booking-Form/typings/booking-inputs";
import { calculateMaxEndDate } from "../utils/booking.utils";
import generateDisabledWeekdays from "./functions/generate-disabled-weekdays.function";
import useTimeFormat from "../../../hooks/useTimeFormat/useTimeFormat";
import { getValidEndTime } from "./functions/getValidEndTime.function";

const DateTimePicker: React.FC = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { bookingStart, bookingEnd, bookingFrom, bookingTo, frequence, mode } = useSelector(
    (state: RootState) => state.booking.inputs
  );
  const { meta: metaData } = useSelector((state: RootState) => state.login.userInformation.company);
  const { timeFormat, dateFormat } = useTimeFormat();

  const [tempStart, setTempStart] = useState<string | null>(bookingStart);

  // checks if sa and so are allowed and generates the datepicker invalid string
  const disabledOnWeekDays = useMemo(
    () =>
      generateDisabledWeekdays({
        bookingsOnSaturdaysAllowed: metaData.bookingsOnSaturdaysAllowed,
        bookingsOnSundaysAllowed: metaData.bookingsOnSundaysAllowed
      }),
    [metaData]
  );

  const maxDate: string = calculateMaxEndDate({ metaData, bookingMode: mode });

  function onChangeStartDate(picker: any) {
    if (frequence === Frequence.SINGLE) {
      const bookingFromPickerValue = picker.value;
      const specificDays = [bookingFromPickerValue];

      if (mode === BookingType.VEHICLE) {
        const newStart = DateTime.fromISO(bookingFromPickerValue);
        const currentEnd = DateTime.fromISO(bookingTo ?? bookingFromPickerValue);
        const bookingToPicker = newStart > currentEnd ? bookingFromPickerValue : bookingTo;

        dispatch(
          setInputs({
            bookingFrom: bookingFromPickerValue,
            bookingTo: bookingToPicker,
            specificDays
          })
        );
        return;
      }

      dispatch(
        setInputs({
          bookingFrom: bookingFromPickerValue,
          bookingTo: bookingFromPickerValue,
          specificDays
        })
      );
      return;
    }

    const bookingFromPicker = picker.value;
    dispatch(setInputs({ bookingFrom: bookingFromPicker }));
  }

  function onChangeBookingTime(picker: MbscDatepickerChangeEvent) {
    if (!picker.value) return;

    if (Array.isArray(picker.value)) {
      setTempStart(DateTime.fromJSDate(picker.value[0] as Date).toFormat("HH:mm"));

      const newStart = picker.value[0]
        ? DateTime.fromJSDate(picker.value[0] as Date).toFormat("HH:mm")
        : "08:00";
      let newEnd = picker.value[1]
        ? DateTime.fromJSDate(picker.value[1] as Date).toFormat("HH:mm")
        : "17:00";

      // if conference zone booking, set the default duration by 60 mins
      if (mode === BookingType.CONFERENCEZONE && newStart !== tempStart) {
        newEnd = DateTime.fromJSDate(picker.value[0] as Date)
          .plus({ hour: 1 })
          .toFormat("HH:mm");
      }

      dispatch(setInputs({ bookingStart: newStart, bookingEnd: newEnd }));
    }
  }

  return (
    <Grid2 container>
      <Grid2 size={12}>
        <Box
          sx={{
            alignItems: { xs: "stretch", sm: "flex-end" },
            columnGap: 2,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            flexWrap: "wrap",
            rowGap: 2
          }}
        >
          {frequence !== Frequence.SPECIFIC && (
            <FormControl data-testid="datepicker-control" sx={{ flex: { sm: "0 1 auto" } }}>
              <FormLabel component="legend" style={{ marginLeft: "1em" }}>
                {t("Start Date")}
              </FormLabel>
              <Datepicker
                aria-label={"datepicker"}
                data-testid={"date-single"}
                invalid={disabledOnWeekDays}
                value={bookingFrom}
                onChange={onChangeStartDate}
                min={DateTime.now().toFormat("yyyy-MM-dd")}
                max={maxDate}
                returnFormat="iso8601"
                dateFormat={dateFormat.toUpperCase()}
                locale={locale[i18n.language]}
              />
            </FormControl>
          )}
          {frequence !== Frequence.SPECIFIC &&
            (frequence !== Frequence.SINGLE || mode === BookingType.VEHICLE) && (
              <FormControl data-testid="datepicker-control1" sx={{ flex: { sm: "0 1 auto" } }}>
                <FormLabel component="legend" style={{ marginLeft: "1em" }}>
                  {t("End Date")}
                </FormLabel>
                <Datepicker
                  aria-label={"datepicker1"}
                  data-testid={"date-single1"}
                  invalid={disabledOnWeekDays}
                  value={bookingTo}
                  onChange={picker => {
                    const bookingToPicker =
                      (picker.value as string) || DateTime.now().toFormat("yyyy-MM-dd");
                    dispatch(setInputs({ bookingTo: bookingToPicker }));
                  }}
                  min={DateTime.fromISO(bookingFrom ?? DateTime.now().toISO()).toFormat(
                    "yyyy-MM-dd"
                  )}
                  max={maxDate}
                  returnFormat="iso8601"
                  dateFormat={dateFormat.toUpperCase()}
                  locale={locale[i18n.language]}
                />
              </FormControl>
            )}
          <FormControl
            data-testid="timepicker-control"
            sx={{
              flex: { sm: "1 1 220px" },
              minWidth: { sm: 200 },
              width: { xs: "100%", sm: "auto" }
            }}
          >
            <FormLabel component="legend" style={{ marginLeft: "1em" }}>
              {t("Time")}
            </FormLabel>
            <Datepicker
              controls={["time"]}
              data-testid={"timePicker"}
              locale={locale[i18n.language]}
              onChange={onChangeBookingTime}
              select="range"
              stepMinute={15}
              timeFormat={timeFormat}
              value={[bookingStart, bookingEnd]}
              valid={[
                {
                  start: "00:00",
                  end: getValidEndTime(maxDate, bookingTo)
                }
              ]}
            />
          </FormControl>
        </Box>
      </Grid2>
    </Grid2>
  );
};

export default DateTimePicker;
